/**
 * AgentEditor — постоянный редактор агента клиента: главная инструкция + база знаний.
 *
 * Зачем отдельно от KbReview в StageCockpit: там правка была доступна только на
 * стадиях сборки/тестирования, и чтобы поправить промпт после запуска, оператору
 * приходилось откатывать сделку назад по воронке. Этот блок висит на карточке
 * ВСЕГДА, на любой стадии — правка инструкции не должна зависеть от этапа воронки.
 *
 * «Инструкция» — это топик `system_prompt`: тот самый текст, который уезжает в
 * Тоню как системный промпт (`tenant_prompts.system_prompt`). Остальные шесть
 * топиков — фактура, они на вкладке «База знаний» (переиспользуем KbReview).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  BookOpen,
  Braces,
  Code2,
  Hash,
  Loader2,
  MessageSquare,
  Redo2,
  Save,
  Sparkles,
  Table,
  Undo2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { KbReview } from '@/components/KbReview'
import { ChannelsGrid } from '@/components/ChannelsGrid'
import { TestChat } from '@/components/TestChat'
import { AgentsBar } from '@/components/AgentsBar'
import { SpecialistPanel } from '@/components/SpecialistPanel'
import { WidgetPanel } from '@/components/WidgetPanel'
import { DialogsPanel } from '@/components/DialogsPanel'
import { RequestsPanel } from '@/components/RequestsPanel'
import { kbApi, type KbTopicItem } from '@/api/client'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const PROMPT_TOPIC = 'system_prompt'
const PROMPT_TITLE = 'Персона (системный промпт)'

type Tab = 'instruction' | 'kb' | 'channels' | 'integrations' | 'widget'
  | 'dialogs' | 'requests' | 'test'

/** Кнопка панели редактора: иконка в квадрате, с подсказкой. */
function ToolBtn({
  title,
  onClick,
  disabled,
  children,
}: {
  title: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded border text-slate-500 hover:bg-accent hover:text-slate-900 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
    >
      {children}
    </button>
  )
}

export function AgentEditor({
  dealId, onEdited, clientTenantId = null,
}: { dealId: string; onEdited?: () => void; clientTenantId?: string | null }) {
  const [tab, setTab] = useState<Tab>('instruction')
  // Специалисты клиента (08.08): null — правим основного агента, иначе slug
  // выбранного специалиста. Появляются, только когда кабинет клиента заведён:
  // до этого тенанта нет, и заводить специалистов не к чему.
  const [agent, setAgent] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  // Черновик правим локально; пока оператор не трогал поле — тянемся за сервером.
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  // Правка сохранена, но до бота не доехала — молчать об этом нельзя.
  const [notDelivered, setNotDelivered] = useState<string | null>(null)
  const [scenarioBusy, setScenarioBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()

  // Чей сценарий ведёт разговор (10.08): наш встроенный или инструкция клиента.
  const { data: scenario } = useQuery({
    queryKey: ['scenario-mode', dealId],
    queryFn: () => kbApi.scenarioMode(dealId),
    staleTime: 60 * 1000,
    enabled: !USE_MOCK,
  })
  const ownScenario = scenario?.own_scenario ?? false

  // Своя история отмены: как только value у textarea контролируется React,
  // родной Ctrl+Z браузера перестаёт работать корректно — держим стеки сами.
  const taRef = useRef<HTMLTextAreaElement>(null)
  const undoStack = useRef<string[]>([])
  const redoStack = useRef<string[]>([])
  const lastPushAt = useRef(0)
  const [histTick, setHistTick] = useState(0)  // чтобы кнопки перерисовывались

  /** Запомнить предыдущее значение. Правки, идущие подряд, схлопываем по времени —
   *  иначе каждая буква становилась бы отдельным шагом отмены. */
  function pushHistory(prev: string, force = false) {
    const now = Date.now()
    if (!force && now - lastPushAt.current < 600) return
    lastPushAt.current = now
    undoStack.current = [...undoStack.current.slice(-99), prev]
    redoStack.current = []
    setHistTick((t) => t + 1)
  }

  function applyDraft(next: string, prev: string, force = false) {
    pushHistory(prev, force)
    setDraft(next)
    setDirty(true)
  }

  function undo() {
    const prev = undoStack.current.pop()
    if (prev === undefined) return
    redoStack.current.push(draft)
    undoStack.current = [...undoStack.current]
    setDraft(prev)
    setDirty(true)
    setHistTick((t) => t + 1)
  }

  function redo() {
    const next = redoStack.current.pop()
    if (next === undefined) return
    undoStack.current = [...undoStack.current, draft]
    setDraft(next)
    setDirty(true)
    setHistTick((t) => t + 1)
  }

  /**
   * Вставка разметки по курсору. `linePrefix` — префикс строки (заголовки,
   * комментарии), `wrap` — обёртка выделенного текста (код, переменная).
   */
  function insert(kind: 'comment' | 'h1' | 'h2' | 'code' | 'var' | 'table') {
    const ta = taRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const value = draft
    const selected = value.slice(start, end)

    let next: string
    let caret: number

    if (kind === 'comment' || kind === 'h1' || kind === 'h2') {
      const prefix = kind === 'comment' ? '// ' : kind === 'h1' ? '# ' : '## '
      // Префикс ставим в начало строки, где стоит курсор.
      const lineStart = value.lastIndexOf('\n', start - 1) + 1
      next = value.slice(0, lineStart) + prefix + value.slice(lineStart)
      caret = start + prefix.length
    } else if (kind === 'table') {
      const tpl = '\n| Колонка | Колонка |\n|---|---|\n|  |  |\n'
      next = value.slice(0, start) + tpl + value.slice(end)
      caret = start + tpl.length
    } else {
      const [open, close] = kind === 'code' ? ['```\n', '\n```'] : ['{{', '}}']
      next = value.slice(0, start) + open + selected + close + value.slice(end)
      caret = start + open.length + selected.length
    }

    applyDraft(next, value, true)
    // Возвращаем фокус и ставим курсор туда, где удобно печатать дальше.
    requestAnimationFrame(() => {
      ta.focus()
      ta.setSelectionRange(caret, caret)
    })
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['kb', dealId],
    queryFn: async (): Promise<KbTopicItem[]> => {
      if (USE_MOCK) return []
      return kbApi.list(dealId)
    },
    staleTime: 30 * 1000,
  })

  const prompt = data?.find((t) => t.topic === PROMPT_TOPIC)
  const serverText = prompt?.content_md ?? ''

  useEffect(() => {
    if (!dirty) setDraft(serverText)
  }, [serverText, dirty])

  async function save() {
    setSaving(true)
    setError(null)
    setNotDelivered(null)
    try {
      // Сохранение сразу уходит боту (09.08). Ответ говорит, дошло ли: раньше
      // между «Сохранено» и «бот работает по этому тексту» была пропасть, ничем
      // в интерфейсе не обозначенная.
      const res = USE_MOCK
        ? { published: true }
        : await kbApi.save(dealId, PROMPT_TOPIC, draft, PROMPT_TITLE)
      await qc.invalidateQueries({ queryKey: ['kb', dealId] })
      setDirty(false)
      setSaved(true)
      if (res && res.published === false) setNotDelivered(res.publish_note ?? null)
      setTimeout(() => setSaved(false), 4000)
      onEdited?.()
    } catch {
      setError('Не удалось сохранить. Проверьте связь и попробуйте ещё раз.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            Агент клиента
          </CardTitle>
          <div className="flex gap-1 rounded-md border p-0.5">
            <button
              type="button"
              onClick={() => setTab('instruction')}
              className={`px-3 py-1 text-xs rounded ${tab === 'instruction' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              Инструкция
            </button>
            <button
              type="button"
              onClick={() => setTab('kb')}
              className={`px-3 py-1 text-xs rounded ${tab === 'kb' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              База знаний
            </button>
            <button
              type="button"
              onClick={() => setTab('channels')}
              className={`px-3 py-1 text-xs rounded ${tab === 'channels' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              Каналы
            </button>
            <button
              type="button"
              onClick={() => setTab('integrations')}
              className={`px-3 py-1 text-xs rounded ${tab === 'integrations' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              Интеграции
            </button>
            <button
              type="button"
              onClick={() => setTab('dialogs')}
              className={`px-3 py-1 text-xs rounded ${tab === 'dialogs' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              Диалоги
            </button>
            <button
              type="button"
              onClick={() => setTab('requests')}
              className={`px-3 py-1 text-xs rounded ${tab === 'requests' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              Заявки
            </button>
            <button
              type="button"
              onClick={() => setTab('widget')}
              className={`px-3 py-1 text-xs rounded ${tab === 'widget' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
            >
              Виджет на сайте
            </button>
            {/* Выделяем: это то, ради чего сюда чаще всего заходят после правки. */}
            <button
              type="button"
              onClick={() => setTab('test')}
              className={`flex items-center gap-1 px-3 py-1 text-xs rounded ${
                tab === 'test'
                  ? 'bg-slate-900 text-white font-medium'
                  : 'bg-slate-900/5 text-slate-900 font-medium hover:bg-slate-900/10'
              }`}
            >
              <MessageSquare className="h-3 w-3" />
              Тестировать бота
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Инструкция определяет поведение агента и его ответы. Правится на любой стадии.
        </p>
        {clientTenantId && (
          <div className="pt-2">
            <AgentsBar
              parent={clientTenantId}
              selected={agent}
              onSelect={setAgent}
              mainLabel="Основной агент"
            />
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-3">
        {agent ? (
          <SpecialistPanel slug={agent} />
        ) : tab === 'dialogs' ? (
          clientTenantId
            ? <DialogsPanel slug={clientTenantId} />
            : <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Переписки появятся, когда у сделки будет агент клиента.
              </div>
        ) : tab === 'requests' ? (
          clientTenantId
            ? <RequestsPanel slug={clientTenantId} />
            : <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Заявки появятся, когда у сделки будет агент клиента.
              </div>
        ) : tab === 'widget' ? (
          clientTenantId
            ? <WidgetPanel slug={clientTenantId} />
            : <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Виджет ставится агенту клиента — у этой сделки его пока нет.
              </div>
        ) : tab === 'test' ? (
          <TestChat dealId={dealId} />
        ) : tab === 'integrations' ? (
          <ChannelsGrid dealId={dealId} kind="integration" />
        ) : tab === 'channels' ? (
          <ChannelsGrid dealId={dealId} kind="channel" />
        ) : tab === 'kb' ? (
          <KbReview dealId={dealId} onEdited={onEdited} />
        ) : isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загружаю инструкцию…
          </div>
        ) : isError ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Не удалось загрузить инструкцию.
          </div>
        ) : (
          <>
            {/* Панель управления промптом: отмена/повтор слева, вставка
                разметки справа — как в редакторе, к которому привык основатель. */}
            <div className="flex items-start gap-2">
              <div className="flex gap-1">
                <ToolBtn
                  title="Отменить"
                  disabled={undoStack.current.length === 0}
                  onClick={undo}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                </ToolBtn>
                <ToolBtn
                  title="Повторить"
                  disabled={redoStack.current.length === 0}
                  onClick={redo}
                >
                  <Redo2 className="h-3.5 w-3.5" />
                </ToolBtn>
              </div>

              <div className="flex-1" />

              <div className="flex gap-1">
                <ToolBtn title="Комментарий — строка, которую агент игнорирует" onClick={() => insert('comment')}>
                  <span className="text-xs font-mono">//</span>
                </ToolBtn>
                <ToolBtn title="Заголовок раздела" onClick={() => insert('h1')}>
                  <Hash className="h-3.5 w-3.5" />
                </ToolBtn>
                <ToolBtn title="Подзаголовок" onClick={() => insert('h2')}>
                  <span className="text-xs font-mono">##</span>
                </ToolBtn>
                <ToolBtn title="Блок кода или примера" onClick={() => insert('code')}>
                  <Code2 className="h-3.5 w-3.5" />
                </ToolBtn>
                <ToolBtn title="Подстановка — {{переменная}}" onClick={() => insert('var')}>
                  <Braces className="h-3.5 w-3.5" />
                </ToolBtn>
                <ToolBtn title="Таблица" onClick={() => insert('table')}>
                  <Table className="h-3.5 w-3.5" />
                </ToolBtn>
              </div>
            </div>

            <textarea
              ref={taRef}
              className="w-full min-h-[320px] rounded-md border bg-background px-3 py-2 text-sm font-mono leading-relaxed"
              value={draft}
              onChange={(e) => applyDraft(e.target.value, draft)}
              onKeyDown={(e) => {
                // Ctrl/Cmd+Z и Ctrl/Cmd+Shift+Z — привычные сочетания поверх
                // своей истории (родная отмена в контролируемом поле не работает).
                const mod = e.ctrlKey || e.metaKey
                if (mod && e.key.toLowerCase() === 'z') {
                  e.preventDefault()
                  if (e.shiftKey) redo()
                  else undo()
                }
              }}
              placeholder={`# Персона\n\nТы — цифровой сотрудник компании…`}
            />

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-xs text-muted-foreground">
                {draft.length.toLocaleString('ru-RU')} знаков
                {dirty
                  ? ' · есть несохранённые правки'
                  : prompt?.current_status === 'approved' && ' · бот отвечает по этому тексту'}
              </span>
              <div className="flex items-center gap-2">
                {saved && !notDelivered && (
                  <span className="text-xs text-green-700">Сохранено — бот обновлён</span>
                )}
                <Button size="sm" className="gap-2" disabled={saving || !dirty} onClick={save}>
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Сохранить
                </Button>
              </div>
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={ownScenario}
                  disabled={scenarioBusy}
                  onChange={async (e) => {
                    const next = e.target.checked
                    setScenarioBusy(true)
                    try {
                      await kbApi.setScenarioMode(dealId, next)
                      await qc.invalidateQueries({ queryKey: ['scenario-mode', dealId] })
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Не удалось переключить.')
                    } finally { setScenarioBusy(false) }
                  }}
                />
                <span>
                  <span className="block text-sm font-medium">
                    Разговор ведёт эта инструкция
                  </span>
                  <span className="block text-[11px] text-muted-foreground leading-relaxed">
                    По умолчанию порядок шагов задаёт наш встроенный сценарий
                    консультанта: ответить на вопрос по фактам и задать один
                    уточняющий. Если в инструкции расписан свой порядок — список
                    вопросов, вызов расчёта, — включите: наш сценарий уступит ей.
                    Правила голоса, запрет выдумывать факты и передача человеку
                    работают в обоих случаях.
                  </span>
                </span>
              </label>
            </div>

            {notDelivered && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {notDelivered}
              </div>
            )}

            {error && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {error}
              </div>
            )}

            {/* Честно предупреждаем о цене правки: сохранение создаёт новую версию
                топика, возвращает Ворота 2 в «не пройдены» и расходится с хэшем,
                к которому привязан тест-гейт. */}
            <div className="flex gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Правка создаёт новую версию и закрывает Ворота 2 — БЗ нужно одобрить заново,
                а тест-прогон повторить. У живого бота инструкция обновится только после
                переодобрения БЗ.
              </span>
            </div>

            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <BookOpen className="h-3 w-3" />
              Факты — цены, услуги, гарантии, FAQ — не здесь, а на вкладке «База знаний».
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}
